import { delayRender, continueRender } from "remotion"
import { FONT_BY_FAMILY } from "@/lib/font-catalog"

// Direct S3 URL for fonts — bucket policy makes assets/fonts/* public.
// No backend call needed, works in Lambda and all environments.
const FONT_S3_BASE = "https://altaraibucket.s3.us-east-2.amazonaws.com/assets/fonts"

/**
 * Maps legacy font family names (used by existing presets and saved projects)
 * to the canonical family names in the font catalog.
 */
const ALIASES: Record<string, string> = {
  "Montserrat": "Montserrat Black",
  "Montserrat-Black": "Montserrat Black",
  "Montserrat-Bold": "Montserrat ExtraBold",
  "Montserrat-SemiBold": "Montserrat Regular",
  "Montserrat-Regular": "Montserrat Regular",
  "Inter": "Inter Bold",
  "Inter-Bold": "Inter Bold",
  "Inter-Regular": "Inter Bold",
  "Inter-Variable": "Inter Bold",
  "Roboto": "Roboto Black",
  "Roboto-Black": "Roboto Black",
  "Roboto-Bold": "Roboto Black",
  "Roboto-Regular": "Roboto Black",
  "Poppins": "Montserrat Black",
  "Poppins-Bold": "Montserrat Black",
  "Poppins-Regular": "Montserrat Regular",
  "Poppins-SemiBold": "Montserrat Black",
  "BebasNeue": "Bebas Neue Regular",
  "Bebas Neue": "Bebas Neue Regular",
  "Oswald": "Anton Regular",
  "Oswald-Bold": "Anton Regular",
  "Oswald-SemiBold": "Anton Regular",
  "KomikaAxis": "Komika Title - Axis",
  "KomikaAxis-Bold": "Komika Title - Axis",
  "Komika Axis": "Komika Title - Axis",
  "SplineSans": "Inter Bold",
  "SplineSans-Bold": "Inter Bold",
}

const loadedFonts = new Set<string>()
const loadingFonts = new Map<string, Promise<void>>()

/**
 * Build the direct S3 URL for a font file.
 * Falls back to the entry's hydrated URL if available.
 */
function getFontUrl(entry: { filename: string; url: string }): string {
  // Direct public S3 URL — no backend call, works everywhere including Lambda
  if (entry.filename) {
    return `${FONT_S3_BASE}/${entry.filename}`
  }
  // Fallback to hydrated URL (from backend) if filename is missing
  return entry.url
}

/**
 * Load a font by family name for use in Remotion compositions.
 * Uses delayRender/continueRender so Remotion waits for the font
 * before rendering any frame — no more fallback font flash.
 */
export function loadFont(family: string): void {
  if (loadedFonts.has(family)) return
  if (loadingFonts.has(family)) return
  if (typeof document === "undefined" || typeof FontFace === "undefined") return

  const resolved = ALIASES[family] ?? family
  const entry = FONT_BY_FAMILY.get(resolved)

  if (!entry) {
    console.warn(`Font "${family}" not found in catalog, skipping load`)
    return
  }

  const fontUrl = getFontUrl(entry)
  if (!fontUrl) {
    console.warn(`Font "${family}" has no URL, skipping load`)
    return
  }

  const handle = delayRender(`Loading font: ${family}`)

  const promise = new FontFace(family, `url(${fontUrl})`)
    .load()
    .then((loaded) => {
      document.fonts.add(loaded)
      loadedFonts.add(family)
    })
    .catch((err) => {
      console.warn(`Failed to load font "${family}":`, err)
    })
    .finally(() => {
      loadingFonts.delete(family)
      continueRender(handle)
    })

  loadingFonts.set(family, promise)
}

/**
 * Ensure a font family is loaded, with a fallback.
 */
export function ensureFont(family: string, fallback: string = "sans-serif"): string {
  loadFont(family)
  return `${family}, ${fallback}`
}
